import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
} from "reactstrap";
import { Link, useHistory } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";

import { BreadCrumb } from "../../components/Common";
import { AdminSentNotificationDto, PaginateQuery } from "../../interfaces";
import {
  ActionButton,
  AppTable,
  ColumnHeader,
  DateColumn,
} from "../../components";
import { ActionTypeEnum, NotificationAudienceEnum, NotificationTypeEnum } from "../../enums";
import DeleteModal from "../../components/Common/DeleteModal";
import {
  useGetAdminSentNotificationsQuery,
  useDeleteNotificationMutation,
} from "../../store/api/notificationApi";
// @ts-ignore
import "./NotificationsPage.scss";

const AUDIENCE_BADGE_COLOR: Record<string, string> = {
  [NotificationAudienceEnum.STUDENT]: "info",
  [NotificationAudienceEnum.PARENT]: "primary",
  [NotificationAudienceEnum.TEACHER_ADMIN]: "success",
  [NotificationAudienceEnum.SYSTEM_ADMIN]: "danger",
  [NotificationAudienceEnum.HEAD_TEACHER_ADMIN]: "warning",
  [NotificationAudienceEnum.TEACHER]: "secondary",
  [NotificationAudienceEnum.GRADE]: "dark",
  [NotificationAudienceEnum.USER]: "secondary",
  [NotificationAudienceEnum.PARENT_NO_PURCHASE]: "primary",
  [NotificationAudienceEnum.STUDENT_NO_PURCHASE]: "info",
};

const formatPercent = (part: number, total: number): string => {
  if (!total || total <= 0) {
    return "0%";
  }
  const percentage = (part / total) * 100;
  if (percentage === 0 || percentage === 100) {
    return `${Math.round(percentage)}%`;
  }
  return `${percentage.toFixed(1)}%`;
};

const isPendingNotification = (row: AdminSentNotificationDto): boolean =>
  Boolean(row.scheduleDate) && !row.isCronExecuted;

const TYPE_BADGE_COLOR: Record<string, string> = {
  [NotificationTypeEnum.ADMIN_SENT_NOTIFICATION]: "primary",
  [NotificationTypeEnum.SUBJECT_NOTE_CREATED]: "info",
  [NotificationTypeEnum.LECTURE_UPLOADED]: "success",
};

const TypeBadge: React.FC<{
  type?: string;
  t: (key: string) => string;
}> = ({ type, t }) => {
  if (!type) {
    return <span className="text-muted">—</span>;
  }
  const color = TYPE_BADGE_COLOR[type] || "secondary";
  return (
    <span className={`badge bg-soft-${color} text-${color}`}>{t(type)}</span>
  );
};

const NotificationCell: React.FC<{
  row: AdminSentNotificationDto;
  language: string;
}> = ({ row, language }) => {
  const title = language === "ar" ? row.arabicTitle : row.title;
  const body = language === "ar" ? row.arabicBody : row.body;

  return (
    <div className="d-flex gap-2 align-items-start py-1">
      {row.imageUrl ? (
        <img src={row.imageUrl} alt="" className="notifications-thumb" />
      ) : (
        <span className="avatar-xs notifications-icon">
          <span className="avatar-title rounded-2 bg-soft-primary text-primary">
            <i className="ri-notification-3-line"></i>
          </span>
        </span>
      )}
      <div className="min-w-0">
        <div className="fw-semibold text-body notifications-title" title={title}>
          {title || "—"}
        </div>
        {body ? (
          <div className="text-muted fs-12 notifications-body-preview" title={body}>
            {body}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const AudienceCell: React.FC<{
  audience?: NotificationAudienceEnum[];
  t: (key: string) => string;
}> = ({ audience, t }) => {
  if (!audience?.length) {
    return <span className="text-muted">—</span>;
  }

  return (
    <div className="d-flex flex-wrap gap-1 py-1">
      {audience.map((value) => {
        const color = AUDIENCE_BADGE_COLOR[value] || "secondary";
        return (
          <span
            key={value}
            className={`badge bg-soft-${color} text-${color} text-capitalize`}
          >
            {t(value)}
          </span>
        );
      })}
    </div>
  );
};

const FiltersCell: React.FC<{
  labels?: AdminSentNotificationDto["targetingLabels"];
  language: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}> = ({ labels, language, t }) => {
  const langKey = language === "ar" ? "ar" : "en";
  const nameOf = (value?: { en?: string; ar?: string }) =>
    (langKey === "ar" ? value?.ar : value?.en) || value?.en || value?.ar || "";

  const purchaseKey =
    labels?.purchaseFilter === "purchase"
      ? "withPurchase"
      : labels?.purchaseFilter === "no_purchase"
        ? "withoutPurchase"
        : labels?.purchaseFilter === "all"
          ? "all"
          : "";

  const rows = [
    labels?.educationSystem && {
      label: t("educationSystem"),
      value: nameOf(labels.educationSystem),
    },
    labels?.curriculum && {
      label: t("curriculum"),
      value: nameOf(labels.curriculum),
    },
    labels?.course && { label: t("course"), value: nameOf(labels.course) },
    labels?.educationalStage && {
      label: t("educationalStage"),
      value: nameOf(labels.educationalStage),
    },
    labels?.grade && { label: t("grade"), value: nameOf(labels.grade) },
    labels?.subject && { label: t("subject"), value: nameOf(labels.subject) },
    purchaseKey
      ? {
          label: t("purchaseStatus"),
          value: t(purchaseKey),
        }
      : null,
    labels?.specificUsersCount
      ? {
          label: t("user"),
          value: t("specificRecipients", {
            count: labels.specificUsersCount,
          }),
        }
      : null,
  ].filter((row): row is { label: string; value: string } => Boolean(row?.value));

  if (!rows.length) {
    return <span className="text-muted">—</span>;
  }

  return (
    <div className="notifications-filters">
      {rows.map((row) => (
        <div key={row.label} className="notifications-filters__row">
          <span className="notifications-filters__label">{row.label}</span>
          <span className="notifications-filters__value">{row.value}</span>
        </div>
      ))}
    </div>
  );
};

const StatusCell: React.FC<{
  row: AdminSentNotificationDto;
  t: (key: string) => string;
}> = ({ row, t }) => {
  const pending = isPendingNotification(row);

  return (
    <div className="py-1">
      <span
        className={`badge ${
          pending
            ? "bg-soft-warning text-warning"
            : "bg-soft-success text-success"
        }`}
      >
        <i
          className={`${
            pending ? "ri-time-line" : "ri-checkbox-circle-line"
          } me-1 align-middle`}
        ></i>
        {t(pending ? "pending" : "sent")}
      </span>
      <div className="text-muted fs-12 mt-1">
        {row.scheduleDate
          ? `${t("scheduled")}: ${new Date(
              row.scheduleDate,
            ).toLocaleString()}`
          : t("notScheduled")}
      </div>
    </div>
  );
};

const DeliveryCell: React.FC<{
  row: AdminSentNotificationDto;
  t: (key: string) => string;
}> = ({ row, t }) => {
  const readPercent = formatPercent(row.readCount, row.receiversCount);
  const unreadPercent = formatPercent(row.unreadCount, row.receiversCount);
  const readWidth =
    row.receiversCount > 0
      ? Math.min(100, (row.readCount / row.receiversCount) * 100)
      : 0;

  return (
    <div className="py-1" style={{ minWidth: 140 }}>
      <div className="fw-semibold">
        {Number(row.receiversCount || 0).toLocaleString()}
      </div>
      <div className="progress notifications-delivery__bar">
        <div
          className="progress-bar bg-success"
          role="progressbar"
          style={{ width: `${readWidth}%` }}
          aria-valuenow={readWidth}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
      <div className="d-flex justify-content-between gap-2 fs-12 text-muted">
        <span title={`${t("read")} ${readPercent}`}>
          {t("read")} {row.readCount}
        </span>
        <span title={`${t("unread")} ${unreadPercent}`}>
          {t("unread")} {row.unreadCount}
        </span>
      </div>
    </div>
  );
};

export const NotificationsPage = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const initState = useMemo(() => new PaginateQuery(), []);

  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [defaultSortFieldId, setDefaultSortFieldId] = useState(2);
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);
  const [defaultPage, setDefaultPage] = useState(1);

  const {
    data: response,
    isLoading: loading,
    refetch,
  } = useGetAdminSentNotificationsQuery(paginateQuery, {
    skip: paginateQuery === initState,
  });

  const [
    deleteNotification,
    {
      isLoading: deleteLoading,
      isSuccess: deleteSuccess,
      error: deleteErrorObj,
    },
  ] = useDeleteNotificationMutation();

  const deleteError = deleteErrorObj
    ? (deleteErrorObj as { data?: { message?: string } })?.data?.message ||
      "An error occurred"
    : undefined;

  const getSortByColumnName = (name: string) => {
    switch (name) {
      case "title":
      case "arabicTitle":
        return 1;
      default:
        return 2;
    }
  };

  const onSearch = useCallback((searchText: string) => {
    setPaginateQuery((current) => ({
      ...current,
      search: searchText,
      page: 1,
    }));
  }, []);

  const onChangePage = useCallback((page: number) => {
    setPaginateQuery((current) => ({ ...current, page }));
  }, []);

  const onChangeRowsPerPage = useCallback(
    (currentRowsPerPage: number, currentPage: number) => {
      setPaginateQuery((current) => ({
        ...current,
        limit: currentRowsPerPage,
        page: currentPage,
      }));
    },
    [],
  );

  const onSort = useCallback(
    (
      column: TableColumn<AdminSentNotificationDto>,
      sortDirection: string,
    ) => {
      const sortField =
        column.id === 1
          ? i18n.language === "ar"
            ? "arabicTitle"
            : "title"
          : "created_at";
      setPaginateQuery((current) => ({
        ...current,
        sortBy: `${sortField}:${sortDirection.toUpperCase()}`,
      }));
    },
    [i18n.language],
  );

  const onDelete = useCallback((id: string) => {
    setDeletingItemId(id);
    setShowDeleteModal(true);
  }, []);

  const onDeleteConfirm = () => {
    if (!deletingItemId) {
      return;
    }
    deleteNotification(deletingItemId);
    setDeletingItemId(null);
  };

  const columns: TableColumn<AdminSentNotificationDto>[] = useMemo(
    () => [
      {
        name: <ColumnHeader title="title" />,
        cell: (row) => (
          <NotificationCell row={row} language={i18n.language} />
        ),
        sortable: true,
        grow: 2.4,
        wrap: true,
        minWidth: "240px",
      },
      {
        name: <ColumnHeader title="createdAt" />,
        cell: (row) => <DateColumn date={row.createdAt} />,
        sortable: true,
        width: "160px",
      },
      {
        name: <ColumnHeader title="type" />,
        cell: (row) => <TypeBadge type={row.type} t={t} />,
        sortable: false,
        wrap: true,
        minWidth: "150px",
      },
      {
        name: <ColumnHeader title="audience" />,
        cell: (row) => <AudienceCell audience={row.audience} t={t} />,
        sortable: false,
        wrap: true,
        minWidth: "160px",
      },
      {
        name: <ColumnHeader title="appliedFilters" />,
        cell: (row) => (
          <FiltersCell
            labels={row.targetingLabels}
            language={i18n.language}
            t={t}
          />
        ),
        sortable: false,
        wrap: true,
        minWidth: "220px",
        grow: 1.4,
      },
      {
        name: <ColumnHeader title="status" />,
        cell: (row) => <StatusCell row={row} t={t} />,
        sortable: false,
        wrap: true,
        minWidth: "170px",
      },
      {
        name: <ColumnHeader title="receiversCount" />,
        cell: (row) => <DeliveryCell row={row} t={t} />,
        sortable: false,
        minWidth: "170px",
      },
      {
        name: <ColumnHeader title="action" />,
        sortable: false,
        width: "110px",
        cell: (row) =>
          isPendingNotification(row) ? (
            <ActionButton
              onAction={() => onDelete(row.id)}
              actionType={ActionTypeEnum.DELETE}
            />
          ) : (
            <span className="text-muted">—</span>
          ),
      },
    ],
    [i18n.language, onDelete, t],
  );

  useEffect(() => {
    document.title = `${t("documentTitle", { pageName: "notifications" })}`;
  }, [t]);

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    const sortBy = queryParams.get("sortBy") || initState.sortBy;
    const [sortField, sortDir] = sortBy.split(":");
    const query: PaginateQuery = {
      limit: queryParams.has("limit")
        ? Number(queryParams.get("limit"))
        : initState.limit,
      page: queryParams.has("page")
        ? Number(queryParams.get("page"))
        : initState.page,
      sortBy,
      search: queryParams.get("search") || initState.search,
    };

    if (sortField && sortDir) {
      setDefaultSortAsc(sortDir === "ASC");
      setDefaultSortFieldId(getSortByColumnName(sortField));
    } else {
      setDefaultSortAsc(false);
      setDefaultSortFieldId(2);
    }

    setDefaultPage(query.page);
    setPaginateQuery(query);
    // Restore table state from the URL once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (paginateQuery === initState) {
      return;
    }
    const params = new URLSearchParams();
    if (paginateQuery.page) {
      params.set("page", String(paginateQuery.page));
    }
    if (paginateQuery.limit) {
      params.set("limit", String(paginateQuery.limit));
    }
    if (paginateQuery.sortBy) {
      params.set("sortBy", paginateQuery.sortBy);
    }
    if (paginateQuery.search) {
      params.set("search", paginateQuery.search);
    }
    const nextSearch = params.toString();
    if (history.location.search.replace(/^\?/, "") !== nextSearch) {
      history.replace({ search: nextSearch });
    }
  }, [history, initState, paginateQuery]);

  useEffect(() => {
    if (deleteSuccess) {
      setDeletingItemId(null);
      setShowDeleteModal(false);
      refetch();
    }
  }, [deleteSuccess, refetch]);

  return (
    <React.Fragment>
      <div className="page-content notifications-page">
        <Container fluid>
          <BreadCrumb title={t("list")} pageTitle={t("notifications")} />
          <Row>
            <Col xxl={12}>
              <Card className="shadow-sm">
                <CardHeader className="bg-light border-bottom-0">
                  <div className="notifications-toolbar">
                    <div className="flex-grow-1 me-auto">
                      <h5 className="card-title mb-1">
                        <i className="ri-notification-3-line me-2 align-middle"></i>
                        {t("notifications")}
                      </h5>
                      <p className="text-muted mb-0 fs-13">
                        {t("notificationsListHint")}
                      </p>
                    </div>
                    <div className="search-box notifications-search">
                      <DebounceInput
                        className="form-control"
                        placeholder={`${t("searchPlaceholder")}...`}
                        minLength={2}
                        debounceTimeout={300}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => onSearch(event.target.value)}
                        value={paginateQuery.search || ""}
                      />
                      <i className="ri-search-line search-icon"></i>
                    </div>
                    <div className="hstack gap-2 flex-shrink-0">
                      <Button
                        color="light"
                        onClick={() => refetch()}
                        title={t("refresh")}
                      >
                        <i className="ri-refresh-line me-1 align-bottom"></i>
                        {t("refresh")}
                      </Button>
                      <Link
                        className="btn btn-primary"
                        to="/notifications/notify"
                      >
                        <i className="ri-add-line me-1 align-bottom"></i>
                        {t("sendNew")}
                      </Link>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <AppTable
                    columns={columns}
                    data={response?.data || []}
                    progressPending={loading}
                    onSort={onSort}
                    onChangeRowsPerPage={onChangeRowsPerPage}
                    onChangePage={onChangePage}
                    defaultSortFieldId={defaultSortFieldId}
                    paginationDefaultPage={defaultPage}
                    defaultSortAsc={defaultSortAsc}
                    paginationTotalRows={response?.meta?.totalItems}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <DeleteModal
        loading={deleteLoading}
        show={showDeleteModal}
        onDeleteClick={onDeleteConfirm}
        onCloseClick={() => {
          setShowDeleteModal(false);
        }}
        error={deleteError}
      />
    </React.Fragment>
  );
};
